import { DestroyRef, inject, Injectable, signal } from '@angular/core';
import { environment } from '@env/environment';
import { Observable } from 'rxjs';
import { io, Socket } from 'socket.io-client';

@Injectable({
  providedIn: 'root',
})
export class RealtimeService {
  private socket: Socket | null = null;
  private destroyRef = inject(DestroyRef);

  readonly isConnected = signal<boolean>(false);

  constructor() {
    this.destroyRef.onDestroy(() => this.disconnect());
  }

  connect(accessToken: string): void {
    this.disconnect();

    const url = new URL(environment.API_URL_BASE);
    const baseOrigin = `${url.protocol}//${url.host}${url.port ? ':' + url.port : ''}`;
    this.socket = io(baseOrigin, {
      path: '/api/ws',
      transports: ['websocket'],
      auth: { token: accessToken },
      autoConnect: false,
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 10000,
    });

    this.socket.on('connect', () => {
      console.log('[WS] conectado');
      this.isConnected.set(true); // Actualiza el signal
    });

    this.socket.on('connect_error', (error) => {
      console.error('[WS] error de conexión:', error);
    });

    this.socket.on('disconnect', (reason) => {
      console.log('[WS] desconectado:', reason);
      this.isConnected.set(false);
    });

    this.socket.connect();
  }

  updateToken(accessToken: string): void {
    if (this.socket) {
      this.socket.auth = { token: accessToken };
    }
  }

  ping(): void {
    this.socket?.emit('ping', {}, (res: any) => console.log('[WS] ACK:', res));
  }

  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket.removeAllListeners();
      this.socket = null;
      this.isConnected.set(false);
    }
  }

  listen<T>(eventName: string): Observable<T> {
    return new Observable<T>((subscriber) => {
      if (!this.socket) {
        subscriber.error('Socket no inicializado');
        return;
      }

      this.socket.on(eventName, (data: T) => {
        subscriber.next(data);
      });

      // Cleanup cuando el componente que escucha se destruye
      return () => this.socket?.off(eventName);
    });
  }

  // Método genérico para enviar eventos
  emit(eventName: string, data: any): void {
    this.socket?.emit(eventName, data);
  }
}
